import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '@h2/ui';
import { Checkbox } from '@h2/ui';
import { useConfigurationStore } from '../../stores/useConfigurationStore';

export interface EditorOptions {
  source: {
    readPermission: boolean;
    writePermission: boolean;
    workTarget: boolean;
    downloadAsFile: boolean;
    updateFromFile: boolean;
    timecodeEdit: boolean;
  };
  target: {
    readPermission: boolean;
    writePermission: boolean;
    workTarget: boolean;
    downloadAsFile: boolean;
    updateFromFile: boolean;
    timecodeEdit: boolean;
  };
  video: {
    fullViewRequired: boolean;
    downloadAllowed: boolean;
  };
  comment: {
    readPermission: boolean;
    writePermission: boolean;
    workTarget: boolean;
  };
}

interface TaskTypeEditorOptionsFieldProps {
  title?: string;
  description?: string;
  hideTitle?: boolean;
}

export const TaskTypeEditorOptionsField: React.FC<TaskTypeEditorOptionsFieldProps> = ({
  title,
  description,
  hideTitle = false
}) => {
  const { formData, updateField } = useConfigurationStore();
  const editorOptions = formData.editorOptions as EditorOptions || {
    source: {
      readPermission: false,
      writePermission: false,
      workTarget: false,
      downloadAsFile: false,
      updateFromFile: false,
      timecodeEdit: false
    },
    target: {
      readPermission: false,
      writePermission: false,
      workTarget: false,
      downloadAsFile: false,
      updateFromFile: false,
      timecodeEdit: false
    },
    video: {
      fullViewRequired: false,
      downloadAllowed: false
    },
    comment: {
      readPermission: false,
      writePermission: false,
      workTarget: false
    }
  };

  const handleSourceChange = (key: keyof EditorOptions['source'], checked: boolean) => {
    updateField('editorOptions', {
      ...editorOptions,
      source: {
        ...editorOptions.source,
        [key]: checked
      }
    });
  };

  const handleTargetChange = (key: keyof EditorOptions['target'], checked: boolean) => {
    updateField('editorOptions', {
      ...editorOptions,
      target: {
        ...editorOptions.target,
        [key]: checked
      }
    });
  };

  const handleVideoChange = (key: keyof EditorOptions['video'], checked: boolean) => {
    updateField('editorOptions', {
      ...editorOptions,
      video: {
        ...editorOptions.video,
        [key]: checked
      }
    });
  };

  const handleCommentChange = (key: keyof EditorOptions['comment'], checked: boolean) => {
    updateField('editorOptions', {
      ...editorOptions,
      comment: {
        ...editorOptions.comment,
        [key]: checked
      }
    });
  };

  // 프리셋 적용 함수들
  const applyScriptPreset = () => {
    updateField('editorOptions', {
      ...editorOptions,
      video: { ...editorOptions.video, fullViewRequired: true },
      source: { ...editorOptions.source, readPermission: true, writePermission: true, workTarget: true, timecodeEdit: true },
      target: { ...editorOptions.target },
      comment: { ...editorOptions.comment }
    });
  };

  const applyTranscriptionPreset = () => {
    updateField('editorOptions', {
      ...editorOptions,
      video: { ...editorOptions.video },
      source: { ...editorOptions.source, readPermission: true, writePermission: true, workTarget: true, timecodeEdit: true },
      target: { ...editorOptions.target },
      comment: { ...editorOptions.comment, readPermission: true }
    });
  };

  const applyTranslationPreset = () => {
    updateField('editorOptions', {
      ...editorOptions,
      video: { ...editorOptions.video },
      source: { ...editorOptions.source, readPermission: true },
      target: { ...editorOptions.target, readPermission: true, writePermission: true, workTarget: true },
      comment: { ...editorOptions.comment, readPermission: true }
    });
  };

  const applyTranscriptionReviewPreset = () => {
    updateField('editorOptions', {
      ...editorOptions,
      video: { ...editorOptions.video },
      source: { ...editorOptions.source, readPermission: true, writePermission: true, workTarget: true, timecodeEdit: true },
      target: { ...editorOptions.target },
      comment: { ...editorOptions.comment, readPermission: true, writePermission: true, workTarget: true }
    });
  };

  const applyTranslationReviewPreset = () => {
    updateField('editorOptions', {
      ...editorOptions,
      video: { ...editorOptions.video },
      source: { ...editorOptions.source, readPermission: true },
      target: { ...editorOptions.target, readPermission: true, writePermission: true, workTarget: true },
      comment: { ...editorOptions.comment, readPermission: true, writePermission: true, workTarget: true }
    });
  };

  const clearAllSettings = () => {
    updateField('editorOptions', {
      source: {
        readPermission: false,
        writePermission: false,
        workTarget: false,
        downloadAsFile: false,
        updateFromFile: false,
        timecodeEdit: false
      },
      target: {
        readPermission: false,
        writePermission: false,
        workTarget: false,
        downloadAsFile: false,
        updateFromFile: false,
        timecodeEdit: false
      },
      video: {
        fullViewRequired: false,
        downloadAllowed: false
      },
      comment: {
        readPermission: false,
        writePermission: false,
        workTarget: false
      }
    });
  };

  return (
    <div className="w-full max-w-none space-y-6">
      {!hideTitle && title && (
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          {description && <p className="text-muted-foreground mt-1">{description}</p>}
        </div>
      )}

      {/* 프리셋 버튼들 */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">빠른 설정</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyScriptPreset}
          >
            스크립트
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyTranscriptionPreset}
          >
            전사
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyTranscriptionReviewPreset}
          >
            전사검수
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyTranslationPreset}
          >
            번역
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyTranslationReviewPreset}
          >
            번역검수
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearAllSettings}
            className="text-muted-foreground hover:text-foreground"
          >
            전체 해제
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 원어 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">원어</CardTitle>
            <CardDescription>원본 언어에 대한 권한 설정</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-read"
                checked={editorOptions.source.readPermission}
                onCheckedChange={(checked) => handleSourceChange('readPermission', checked as boolean)}
              />
              <label htmlFor="source-read" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                읽기 권한
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-write"
                checked={editorOptions.source.writePermission}
                onCheckedChange={(checked) => handleSourceChange('writePermission', checked as boolean)}
              />
              <label htmlFor="source-write" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                쓰기 권한
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-work-target"
                checked={editorOptions.source.workTarget}
                onCheckedChange={(checked) => handleSourceChange('workTarget', checked as boolean)}
              />
              <label htmlFor="source-work-target" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                작업 대상 (반드시 작성, 수정해야함)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-download"
                checked={editorOptions.source.downloadAsFile}
                onCheckedChange={(checked) => handleSourceChange('downloadAsFile', checked as boolean)}
              />
              <label htmlFor="source-download" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                파일로 다운로드
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-update"
                checked={editorOptions.source.updateFromFile}
                onCheckedChange={(checked) => handleSourceChange('updateFromFile', checked as boolean)}
              />
              <label htmlFor="source-update" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                파일로 업데이트
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-timecode"
                checked={editorOptions.source.timecodeEdit}
                onCheckedChange={(checked) => handleSourceChange('timecodeEdit', checked as boolean)}
              />
              <label htmlFor="source-timecode" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                타임코드 수정 여부
              </label>
            </div>
          </CardContent>
        </Card>

        {/* 번역어 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">번역어</CardTitle>
            <CardDescription>번역 언어에 대한 권한 설정</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="target-read"
                checked={editorOptions.target.readPermission}
                onCheckedChange={(checked) => handleTargetChange('readPermission', checked as boolean)}
              />
              <label htmlFor="target-read" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                읽기 권한
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="target-write"
                checked={editorOptions.target.writePermission}
                onCheckedChange={(checked) => handleTargetChange('writePermission', checked as boolean)}
              />
              <label htmlFor="target-write" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                쓰기 권한
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="target-work-target"
                checked={editorOptions.target.workTarget}
                onCheckedChange={(checked) => handleTargetChange('workTarget', checked as boolean)}
              />
              <label htmlFor="target-work-target" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                작업 대상 (반드시 작성, 수정해야함)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="target-download"
                checked={editorOptions.target.downloadAsFile}
                onCheckedChange={(checked) => handleTargetChange('downloadAsFile', checked as boolean)}
              />
              <label htmlFor="target-download" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                파일로 다운로드
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="target-update"
                checked={editorOptions.target.updateFromFile}
                onCheckedChange={(checked) => handleTargetChange('updateFromFile', checked as boolean)}
              />
              <label htmlFor="target-update" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                파일로 업데이트
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="target-timecode"
                checked={editorOptions.target.timecodeEdit}
                onCheckedChange={(checked) => handleTargetChange('timecodeEdit', checked as boolean)}
              />
              <label htmlFor="target-timecode" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                타임코드 수정 여부
              </label>
            </div>
          </CardContent>
        </Card>

        {/* 영상파일 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">영상파일</CardTitle>
            <CardDescription>영상 파일에 대한 접근 권한</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="video-full-view"
                checked={editorOptions.video.fullViewRequired}
                onCheckedChange={(checked) => handleVideoChange('fullViewRequired', checked as boolean)}
              />
              <label htmlFor="video-full-view" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                전체 시청 필수
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="video-download"
                checked={editorOptions.video.downloadAllowed}
                onCheckedChange={(checked) => handleVideoChange('downloadAllowed', checked as boolean)}
              />
              <label htmlFor="video-download" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                다운로드 가능 여부
              </label>
            </div>
          </CardContent>
        </Card>

        {/* 코멘트 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">코멘트</CardTitle>
            <CardDescription>코멘트 기능에 대한 권한 설정</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="comment-read"
                checked={editorOptions.comment.readPermission}
                onCheckedChange={(checked) => handleCommentChange('readPermission', checked as boolean)}
              />
              <label htmlFor="comment-read" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                읽기 권한
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="comment-write"
                checked={editorOptions.comment.writePermission}
                onCheckedChange={(checked) => handleCommentChange('writePermission', checked as boolean)}
              />
              <label htmlFor="comment-write" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                쓰기 권한
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="comment-work-target"
                checked={editorOptions.comment.workTarget}
                onCheckedChange={(checked) => handleCommentChange('workTarget', checked as boolean)}
              />
              <label htmlFor="comment-work-target" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                작업 대상 (반드시 작성해야함)
              </label>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
